/**
 * Local-auth (LocalUser/PlatformAdmin) session state — sessionStorage, matching authConfig.ts's
 * own MSAL cacheLocation choice ("keeps a shared iPad tidier than local storage"). Entirely
 * separate from MSAL's own account state; api.ts's authorizedHeaders checks here first and only
 * falls back to an MSAL-acquired token when there's no local session at all.
 */
export interface LocalSession {
  token: string;
  /** "EntraUser" is a per-tenant SSO sign-in (routes/oidc.ts) — treated like a signed-in tenant
   * LocalUser for routing (tile app), but never a setup admin and never forced to change a password. */
  kind: "LocalUser" | "PlatformAdmin" | "EntraUser";
  mustChangePassword: boolean;
  displayName: string;
  /** PlatformAdmin only: true for the top-level super admin (sees all customers, manages admins). */
  isSuperAdmin?: boolean;
}

const STORAGE_KEY = "ruthless-am-local-session";

export function readLocalSession(): LocalSession | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as LocalSession;
  } catch {
    return null;
  }
}

export function writeLocalSession(session: LocalSession): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

/** Used after a successful change-password, so the forced screen doesn't reappear on the very
 * next render — the token itself is unchanged (change-password doesn't reissue one). */
export function markPasswordChanged(): void {
  const current = readLocalSession();
  if (current) writeLocalSession({ ...current, mustChangePassword: false });
}

export function clearLocalSession(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

// ── Impersonation ("Load site as…") ──────────────────────────────────────────
// The SysAdmin's own session is parked here while they view the site as another user, so Exit can
// restore it exactly. sessionStorage, so it survives a reload but a closed tab ends impersonation.
const IMPERSONATOR_KEY = "ruthless-am-impersonator";

/** Park the current (admin) session and switch to the impersonated one. */
export function startImpersonation(target: LocalSession): void {
  const current = readLocalSession();
  if (current) sessionStorage.setItem(IMPERSONATOR_KEY, JSON.stringify(current));
  try { sessionStorage.removeItem("rd-view"); } catch { /* ignore */ }
  writeLocalSession(target);
}

/** The parked admin session, if an impersonation is in progress. */
export function readImpersonatorBackup(): LocalSession | null {
  const raw = sessionStorage.getItem(IMPERSONATOR_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as LocalSession; } catch { return null; }
}

/** Restore the parked admin session and end impersonation. */
export function endImpersonation(): void {
  const backup = readImpersonatorBackup();
  if (backup) writeLocalSession(backup);
  sessionStorage.removeItem(IMPERSONATOR_KEY);
  try { sessionStorage.removeItem("rd-view"); } catch { /* ignore */ }
}
